import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { ApplicationRef, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ErrorModel,
  GlobalMessageService,
  GlobalMessageType,
  HttpResponseStatus,
  Priority,
  SemanticPathService,
  StateWithClientAuth,
  UserIdService
} from '@spartacus/core';
import { LaunchDialogService } from '@spartacus/storefront';
import { Observable } from "rxjs";
import { filter, take } from 'rxjs/operators';
import { HttpResponseErrorType } from '../../../models/response-error-type.model';
import { CustomHttpErrorHandler } from '../../custom-http.handler';

@Injectable({
  providedIn: 'root',
})
export class CustomBadRequestHandler extends CustomHttpErrorHandler {
  responseStatus = HttpResponseStatus.BAD_REQUEST;
  private dialog?: Observable<any>;
  constructor(
    private globalMessageService: GlobalMessageService,
    private launchDialogService: LaunchDialogService,
    private applicationRef: ApplicationRef,
    protected semanticPathService: SemanticPathService,
    protected store: Store<StateWithClientAuth>,
    protected userIdService: UserIdService,
  ) {
    super();
  }

  handleError(request: HttpRequest<any>, response: HttpErrorResponse): void {
    this.handleTermsNotAccepted(request, response);
    this.handleResetPasswordNotAllowedError(request, response);
    this.handleIllegalArgumentError(request, response);
    this.handleUnknownIdentifierError(request, response);
    this.handleCanNotConnectToErpError(request, response);
    this.handlePasswordPolicyViolationError(request, response);
  }

  protected handleTermsNotAccepted(
    _request: HttpRequest<any>,
    response: HttpErrorResponse,
  ): void {
    this.getErrors(response)
      .filter(
        (error) =>
          error.type ===
          HttpResponseErrorType.TERMS_AND_CONDITIONS_NOT_ACCEPTED_EXCEPTION,
      )
      .forEach(() => {
        const openTermsAndConditionsData = {
          redirectToLogin: false,
          hideButtons: false,
        };

        this.applicationRef.isStable
          .pipe(
            filter((isStable) => isStable),
            take(1),
          )
          .subscribe(() => {
            try {
              this.dialog = this.launchDialogService.openDialog(
                'TERMS_CONDITIONS',
                undefined,
                undefined,
                openTermsAndConditionsData,
              );
              this.dialog?.pipe(take(1)).subscribe();
            } catch {
              this.globalMessageService.add(
                {
                  key: 'httpHandlers.illegalArgumentError',
                  params: { errorMessage: '' },
                },
                GlobalMessageType.MSG_TYPE_ERROR,
              );
            }
          });
      });
  }


  protected handleResetPasswordNotAllowedError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse,
  ): void {
    this.getErrors(response)
      .filter(
        (error) =>
          error.type === HttpResponseErrorType.RESET_PASSWORD_NOT_ALLOWED_ERROR,
      )
      .forEach(() => {
        this.globalMessageService.add(
          { key: 'httpHandlers.resetPasswordNotAllowedError' },
          GlobalMessageType.MSG_TYPE_ERROR,
        );
      });
  }

  protected handleIllegalArgumentError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse,
  ): void {
    this.getErrors(response)
      .filter(
        (error) => error.type === HttpResponseErrorType.ILLEGAL_ARGUMENT_ERROR,
      )
      .forEach((error) => {
        this.globalMessageService.add(
          {
            key: 'httpHandlers.illegalArgumentError',
            params: {
              errorMessage: error.message || '',
            },
          },
          GlobalMessageType.MSG_TYPE_ERROR,
        );
      });
  }

  protected handleUnknownIdentifierError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse,
  ): void {
    this.getErrors(response)
      .filter(
        (error) =>
          error.type === HttpResponseErrorType.UNKNOWN_IDENTIFIER_ERROR,
      )
      .forEach((error) => {
        this.globalMessageService.add(
          {
            key: 'httpHandlers.unknownIdentifierError',
            params: {
              errorMessage: error.message || '',
            },
          },
          GlobalMessageType.MSG_TYPE_ERROR,
        );
      });
  }

  protected handleCanNotConnectToErpError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse,
  ): void {
    this.getErrors(response)
      .filter(
        (error) =>
          error.type === HttpResponseErrorType.BACKEND_OFFLINE_EXCEPTION ||
          error.type === HttpResponseErrorType.JCO_ERROR_COMMUNICATION,
      )
      .forEach((error) => {
        this.globalMessageService.add(
          {
            key: 'httpHandlers.erpTimeout',
          },
          GlobalMessageType.MSG_TYPE_ERROR,
        );
      });
  }

  protected handlePasswordPolicyViolationError(
    _request: HttpRequest<any>,
    response: HttpErrorResponse,
  ): void {
    this.getErrors(response)
      .filter(
        (error) =>
          error.type ===
          HttpResponseErrorType.PASSWORD_POLICY_VIOLATION_EXCEPTION,
      )
      .forEach(() => {
        this.globalMessageService.add(
          {
            key: 'httpHandlers.validationErrors.invalid.passwordPolicyException',
          },
          GlobalMessageType.MSG_TYPE_ERROR,
        );
      });
  }

  protected getErrors(response: HttpErrorResponse): ErrorModel[] {
    return (response.error?.errors || []).filter(
      (error: ErrorModel) => error.type !== 'JaloObjectNoLongerValidError',
    );
  }

  getPriority(): Priority {
    return Priority.LOW;
  }
}
